"use client";

import { useRef } from "react";
import type { ReactElement } from "react";
import { Section } from "../primitives";
import { renderIcon } from "../icons";
import { SectionHeader } from "../shared/SectionHeader";
import { LOOP_COPIES, useLoopTrack } from "../shared/useLoopTrack";
import styles from "./CardMarquee.module.css";
import type { CardMarqueeItem, CardMarqueeProps } from "./CardMarquee.types";

/**
 * Piste de cartes qui déborde de l'écran et boucle sans fin, poussée au
 * geste seulement (doigt, pavé tactile, souris). Aucune animation : le
 * défilement est natif, la boucle recale la position quand on a parcouru
 * une copie entière, donc rien à faire pour `prefers-reduced-motion`.
 *
 * La mécanique de boucle vit dans `shared/useLoopTrack` depuis le 24/09/2026 :
 * elle a été extraite telle quelle pour que `SiteShowcase` en variante
 * `marquee` s'en serve aussi, plutôt que d'en avoir deux copies à corriger.
 */
export function CardMarquee({
  badge,
  title,
  titleAs = "h2",
  text,
  titleText,
  cta,
  headerLayout = "split",
  items,
  tone = "grey",
  id,
}: CardMarqueeProps) {
  const shown = items.slice(0, 8);
  const trackRef = useRef<HTMLDivElement>(null);
  useLoopTrack(trackRef, shown.length);

  const renderCard = (item: CardMarqueeItem, index: number): ReactElement => (
    <li key={index} className={styles.card}>
      {item.icon ? (
        <span className={styles.iconBox} aria-hidden="true">
          {renderIcon(item.icon)}
        </span>
      ) : null}
      {item.media ? (
        <div className={styles.media} data-media-zone="true">
          {item.media}
        </div>
      ) : null}
      {item.label ? <span className={styles.label} data-label="true">{item.label}</span> : null}
      <p className={styles.cardTitle}>{item.title}</p>
      <p className={styles.cardText}>{item.text}</p>
    </li>
  );

  const arrows = (
    <div className={styles.nav}>
      <button type="button" className={styles.navButton} data-marquee-prev="true" aria-label="Cartes précédentes">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <button type="button" className={styles.navButton} data-marquee-next="true" aria-label="Cartes suivantes">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );

  return (
    <Section tone={tone} id={id} bleed className={styles.section}>
      <div className={styles.head}>
        <SectionHeader
          badge={badge}
          title={title}
          titleAs={titleAs}
          titleText={titleText}
          intro={text}
          cta={cta}
          after={arrows}
          layout={headerLayout}
        />
      </div>
      <div ref={trackRef} className={styles.track} data-marquee-track="true">
        {Array.from({ length: LOOP_COPIES }, (_, copy) => (
          <ul
            key={copy}
            className={styles.set}
            data-set={copy}
            aria-hidden={copy === 1 ? undefined : "true"}
          >
            {shown.map(renderCard)}
          </ul>
        ))}
      </div>
    </Section>
  );
}
